// ============================================================================
// Shared Merge Utilities
// ============================================================================
// Extracted from ImportTable.helpers.ts for shared use across ImportTable and Wizard

import type { Job } from "../models";
import type { MergeConflict, MergeConflictOption, MergeResolutions, MergeJobsOptions } from "./mergeJobs.types";
import { GeoData } from "@rogoag/shared-ts";

/**
 * Properties that should be merged when combining jobs.
 * Excludes: id (unique identifier), files (handled separately), and system-generated fields.
 */
export const MERGEABLE_PROPERTIES: (keyof Job)[] = [
	"importGrowerName",
	"importFarmName",
	"importFieldName",
	"importDensity",
	"importDepth",
	"externalTestPackageId",
	"externalFrequencyOfMicros",
	"isReadyToSample",
	"isTestJob",
	"fieldPriority",
	"salespersonIds",
	"branchIds",
	"lastCrop",
	"nextCrop",
	"yieldGoal",
	"dateFieldReadyEstimated",
	"labSubmissionCode",
	"labInstructions",
	"growerPhoneNumber",
	"submissionNotes",
	"submitterEmail",
	"submitterName",
	"eventId",
	"dealIds",
	"microsList",
	"collectBoundary",
	"importLabName",
	"externalLabId",
	"externalId",
	"externalFarmId",
];

/**
 * Human-readable display names for job properties
 */
export const PROPERTY_DISPLAY_NAMES: Partial<Record<keyof Job, string>> = {
	importGrowerName: "Grower",
	importFarmName: "Farm",
	importFieldName: "Field",
	importDensity: "Density",
	importDepth: "Depth",
	externalTestPackageId: "Test Package",
	externalFrequencyOfMicros: "Micros Frequency",
	isReadyToSample: "Ready to Sample",
	isTestJob: "Test Job",
	fieldPriority: "Priority",
	salespersonIds: "Salesperson",
	branchIds: "Branch",
	lastCrop: "Last Crop",
	nextCrop: "Next Crop",
	yieldGoal: "Yield Goal",
	dateFieldReadyEstimated: "Est. Field Ready Date",
	labSubmissionCode: "Lab Submission Code",
	labInstructions: "Lab Instructions",
	growerPhoneNumber: "Grower Phone",
	submissionNotes: "Submission Notes",
	submitterEmail: "Submitter Email",
	submitterName: "Submitter Name",
	eventId: "Event ID",
	dealIds: "Deal",
	microsList: "Micros List",
	collectBoundary: "Collect Boundary",
	importLabName: "Lab Name",
	externalLabId: "External Lab ID",
	externalId: "External ID",
	externalFarmId: "External Farm ID",
};

/**
 * Check if a value is considered "truthy" for merge purposes.
 * Empty strings, 0, false, null, undefined, and empty arrays are falsy.
 */
export function isTruthyForMerge(value: unknown): boolean {
	if (value === null || value === undefined) return false;
	if (value === "") return false;
	if (value === 0) return false;
	if (value === false) return false;
	if (Array.isArray(value) && value.length === 0) return false;
	return true;
}

/**
 * Check if two values are equal for merge conflict detection.
 */
export function areValuesEqual(a: unknown, b: unknown): boolean {
	// Handle arrays
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false;
		return a.every((val, idx) => areValuesEqual(val, b[idx]));
	}

	// Handle dates
	if (a instanceof Date && b instanceof Date) {
		return a.getTime() === b.getTime();
	}

	// Handle objects (shallow comparison)
	if (typeof a === "object" && typeof b === "object" && a !== null && b !== null) {
		return JSON.stringify(a) === JSON.stringify(b);
	}

	return a === b;
}

/**
 * Format a value for display in the conflict resolution modal.
 */
export function formatValueForDisplay(value: unknown): string {
	if (value === null || value === undefined) return "(empty)";
	if (value === "") return "(empty)";
	if (value === true) return "Yes";
	if (value === false) return "No";
	if (value instanceof Date) return value.toLocaleDateString();
	if (Array.isArray(value)) {
		if (value.length === 0) return "(empty)";
		return value.join(", ");
	}
	return String(value);
}

/**
 * Create a label for a job in the conflict resolution UI.
 */
function getJobLabel(job: Job, index: number): string {
	const fieldName = job.importFieldName || job.fieldName || `Job ${index + 1}`;
	return fieldName;
}

/**
 * Detect merge conflicts between multiple jobs.
 * Returns an array of conflicts where multiple jobs have different truthy values.
 */
export function detectMergeConflicts(jobs: Job[]): MergeConflict[] {
	const conflicts: MergeConflict[] = [];

	for (const propKey of MERGEABLE_PROPERTIES) {
		const truthyValues: MergeConflictOption[] = [];

		for (let i = 0; i < jobs.length; i++) {
			const job = jobs[i];
			const value = job[propKey];

			if (isTruthyForMerge(value)) {
				// Check if this value is already in our truthy values (deduplication)
				const existingOption = truthyValues.find((opt) => areValuesEqual(opt.value, value));
				if (!existingOption) {
					truthyValues.push({
						value,
						jobIndex: i,
						jobLabel: getJobLabel(job, i),
					});
				}
			}
		}

		// If there are 2+ different truthy values, we have a conflict
		if (truthyValues.length >= 2) {
			conflicts.push({
				propertyKey: propKey,
				displayName: PROPERTY_DISPLAY_NAMES[propKey] || propKey,
				options: truthyValues,
			});
		}
	}

	return conflicts;
}

/**
 * Compute the merged value for a property using truthy-wins logic.
 * If resolutions are provided and this property has a resolution, use that.
 * Otherwise, find the first truthy value among the jobs.
 */
export function getMergedValue(jobs: Job[], propKey: keyof Job, resolutions?: MergeResolutions): unknown {
	// Check if we have a resolution for this property
	if (resolutions && propKey in resolutions) {
		return resolutions[propKey];
	}

	// Find the first truthy value
	for (const job of jobs) {
		const value = job[propKey];
		if (isTruthyForMerge(value)) {
			return value;
		}
	}

	// No truthy value found, return the first job's value
	return jobs[0]?.[propKey];
}

/**
 * Get the list of mergeable properties.
 */
export function getMergeableProperties(): (keyof Job)[] {
	return [...MERGEABLE_PROPERTIES];
}

/**
 * Merge multiple jobs into a single job.
 * Uses the first job as the base, merges GeoJSON via GeoData.merge(),
 * applies truthy-wins logic for all mergeable properties,
 * and merges fileOptions arrays from all source jobs.
 *
 * @param jobs - Jobs to merge (must have at least 2)
 * @param resolutionsOrOptions - Optional conflict resolutions or options object
 * @returns The merged job (uses the first job's ID)
 */
export function mergeJobs(jobs: Job[], resolutionsOrOptions?: MergeResolutions | MergeJobsOptions): Job {
	if (jobs.length < 2) {
		throw new Error("mergeJobs requires at least 2 jobs");
	}

	// Normalize arguments: accept either MergeResolutions or MergeJobsOptions
	const isMergeJobsOptions =
		resolutionsOrOptions &&
		typeof resolutionsOrOptions === "object" &&
		("resolutions" in resolutionsOrOptions || "skipMergeGeoTypes" in resolutionsOrOptions);
	const options: MergeJobsOptions = isMergeJobsOptions
		? (resolutionsOrOptions as MergeJobsOptions)
		: { resolutions: resolutionsOrOptions as MergeResolutions | undefined };

	const { resolutions, skipMergeGeoTypes } = options;
	const skipSet = new Set(skipMergeGeoTypes || []);

	const baseJob = jobs[0];

	// Helper to merge GeoJSON collections, filtering out undefined values
	const mergeGeoJSON = (collections: (typeof baseJob.files.boundaryGeoJSON)[]) => {
		const valid = collections.filter((c): c is NonNullable<typeof c> => !!c);
		return valid.length > 0 ? GeoData.merge(valid) : undefined;
	};

	// Build merged job
	const mergedJob: Job = {
		...baseJob,
		files: {
			...baseJob.files,
			boundaryGeoJSON: skipSet.has("boundary")
				? baseJob.files?.boundaryGeoJSON
				: mergeGeoJSON(jobs.map((j) => j.files?.boundaryGeoJSON)),
			pointsGeoJSON: skipSet.has("points")
				? baseJob.files?.pointsGeoJSON
				: mergeGeoJSON(jobs.map((j) => j.files?.pointsGeoJSON)),
			zonesGeoJSON: skipSet.has("zones")
				? baseJob.files?.zonesGeoJSON
				: mergeGeoJSON(jobs.map((j) => j.files?.zonesGeoJSON)),
		},
		fileOptions: mergeFileOptions(jobs),
	};

	// Apply truthy-wins logic for all mergeable properties
	for (const propKey of MERGEABLE_PROPERTIES) {
		const mergedValue = getMergedValue(jobs, propKey, resolutions);
		(mergedJob as any)[propKey] = mergedValue;
	}

	return mergedJob;
}

/**
 * Merge fileOptions from multiple jobs, concatenating arrays and deduplicating.
 */
function mergeFileOptions(jobs: Job[]): Job["fileOptions"] {
	const base = jobs[0].fileOptions;
	if (jobs.length === 1) return base;

	// For each fileOptions key, merge arrays from all jobs
	const merged = { ...base };

	for (let i = 1; i < jobs.length; i++) {
		const other = jobs[i].fileOptions;
		if (!other) continue;

		// Merge boundary options
		if (other.boundaryOptions?.length) {
			merged.boundaryOptions = [...(merged.boundaryOptions || []), ...other.boundaryOptions];
		}
		// Merge points options
		if (other.pointsOptions?.length) {
			merged.pointsOptions = [...(merged.pointsOptions || []), ...other.pointsOptions];
		}
		// Merge zones options
		if (other.zonesOptions?.length) {
			merged.zonesOptions = [...(merged.zonesOptions || []), ...other.zonesOptions];
		}
	}

	return merged;
}
